import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ArtworkPreview } from './StudioComponents'
type Candidate = {
  artistId: string
  artworkId: string
  candidateSha256: string
  title: string
  date: string
  artistName: string
  sourceImageUrl: string
  selectionReason: string
  themes: string[]
  styles: string[]
}
type Programme = {
  id: string
  proposal_sha256: string
  budget?: { maxExternalCents: number; maxNarrationCharacters: number }
  proposal: {
    completeWeek: boolean
    weekStart: string
    candidates: Candidate[]
    blocked?: { handle?: string; artworkId: string | null; reason: string }[]
  }
  items?: { candidate_sha256: string; status: string }[]
  costs?: {
    stage: string
    max_external_cents: number
    receipt: { tariffUsd: number; invoiceUsd: null } | null
  }[]
}
type DiscoveryRun = {
  id: string
  week_start: string
  status: string
  programme_id: string | null
  error: string | null
}
const base = '/api/v1/films/programmes'
const cardStatus = (status: string) =>
  ({
    candidate_review: 'Idea · not queued',
    creation_approved: 'Approved · world build requested',
    rejected: 'Declined',
    cancelled: 'Cancelled',
  })[status] || 'Check production board'
export function WeeklyProgrammePanel({
  initiallyOpen = false,
  creationEnabled,
}: {
  initiallyOpen?: boolean
  creationEnabled?: boolean
}) {
  const [open, setOpen] = useState(initiallyOpen)
  const [params] = useSearchParams()
  const programmeParam = params.get('programme') || ''
  const [selected, setSelected] = useState(
    /^[a-f0-9-]{36}$/.test(programmeParam) ? programmeParam : ''
  )
  const [checks, setChecks] = useState<string[]>([])
  const [budget, setBudget] = useState(25)
  const [proposal, setProposal] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [discoveryWeek, setDiscoveryWeek] = useState('')
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['film-programmes'],
    queryFn: async () => (await apiClient.get<{ programmes: Programme[] }>(base)).data.programmes,
    enabled: open,
    retry: false,
  })
  useEffect(() => {
    if (/^[a-f0-9-]{36}$/.test(programmeParam)) setSelected(programmeParam)
  }, [programmeParam])
  useEffect(() => {
    if (!selected && list.data?.length) setSelected(list.data[0].id)
  }, [list.data, selected])
  const detail = useQuery({
    queryKey: ['film-programme', selected],
    queryFn: async () => (await apiClient.get<Programme>(`${base}/${selected}`)).data,
    enabled: open && !!selected,
    retry: false,
  })
  const week = detail.data
  const savedBudgetCents = week?.budget?.maxExternalCents
  useEffect(() => {
    setChecks([])
    setConfirmOpen(false)
  }, [selected])
  useEffect(() => {
    setBudget(savedBudgetCents === undefined ? 25 : savedBudgetCents / 100)
  }, [week?.id, savedBudgetCents])
  const discovery = useQuery({
    queryKey: ['film-discovery'],
    queryFn: async () =>
      (await apiClient.get<{ enabled: boolean; runs: DiscoveryRun[] }>(`${base}/discovery/runs`))
        .data,
    enabled: open,
    retry: false,
    refetchInterval: open ? 15000 : false,
  })
  async function action(run: () => Promise<void>) {
    setBusy(true)
    setMessage('')
    try {
      await run()
      await Promise.all(
        [
          'film-programmes',
          'film-programme',
          'film-discovery',
          'content-studio',
          'world-overview',
        ].map((key) => qc.invalidateQueries({ queryKey: [key] }))
      )
    } catch (e) {
      const error = e as { response?: { data?: { error?: string } }; message?: string }
      setMessage(error.response?.data?.error || error.message || 'Programme action failed')
    } finally {
      setBusy(false)
    }
  }
  const picked = week?.proposal.candidates.filter((c) => checks.includes(c.candidateSha256)) || []
  function beginApproval() {
    setConfirmOpen(true)
  }
  return (
    <section className="space-y-5">
      {initiallyOpen ? (
        <div>
          <h2 className="text-xl font-semibold">Choose artwork concepts</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Select the concepts you want and approve creation once. We prepare the original, refresh
            Picasso and artist context, and build the world automatically. Only small previews load
            here. Films are optional — choose them from your world library later.
          </p>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setOpen(!open)}>
          Artwork ideas {open ? '−' : '+'}
        </Button>
      )}
      {open && (
        <>
          {(message || list.error || detail.error || discovery.error) && (
            <div role="status" className="rounded-xl border bg-card p-4 text-sm">
              {message || 'Artwork planning data unavailable. Retry before approving sources.'}
            </div>
          )}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <label className="min-w-0 flex-1 text-sm">
              Artwork batch
              <select
                aria-label="Candidate week"
                className="mt-2 min-h-11 w-full rounded-lg border bg-background px-3"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                <option value="">Choose an artwork batch</option>
                {list.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    Week of {p.proposal.weekStart} · {p.proposal.candidates.length} artwork ideas
                  </option>
                ))}
              </select>
            </label>
            <Link className="py-3 text-sm underline" to="/app/content-studio?view=runs">
              View production runs →
            </Link>
          </div>
          {!list.isPending && !list.error && !list.data?.length && (
            <p className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
              No artwork ideas yet. Use Plan a week to request an automatic discovery batch.
            </p>
          )}
          {week && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-muted/30 p-4">
                <p className="text-sm">
                  <strong>{checks.length} selected</strong> · Automatic world build. No film or post
                  yet.
                </p>
                <Button disabled={busy || !checks.length} onClick={beginApproval}>
                  Create worlds{checks.length ? ` (${checks.length})` : ''}
                </Button>
              </div>
              {week.items?.some((item) => item.status === 'creation_approved') && (
                <div
                  role="status"
                  className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950"
                >
                  <p className="font-semibold">Creation approved</p>
                  <p className="mt-1">
                    {creationEnabled
                      ? 'The runner can start eligible productions. Follow progress in Runs.'
                      : 'Your approval is saved. Production is paused by the administrator; there is nothing else you need to approve to create these.'}
                  </p>
                  <Link
                    className="mt-2 inline-block font-medium underline"
                    to={`/app/content-studio?view=runs&programme=${selected}`}
                  >
                    View production progress →
                  </Link>
                </div>
              )}
              {!week.proposal.completeWeek && (
                <p className="text-sm text-muted-foreground">
                  Small batch: {week.proposal.candidates.length} artwork ideas, not a full seven-day
                  programme. Suggested days are not scheduled posts.
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {week.proposal.candidates.map((c) => {
                  const status =
                    week.items?.find((item) => item.candidate_sha256 === c.candidateSha256)
                      ?.status || 'candidate_review'
                  return (
                    <article
                      key={c.candidateSha256}
                      className="flex min-w-0 flex-col gap-3 rounded-2xl border bg-card p-4"
                    >
                      <ArtworkPreview title={c.title} sourceUrl={c.sourceImageUrl} />
                      <div className="space-y-2">
                        <span className="inline-flex rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                          {cardStatus(status)}
                        </span>
                        <h3 className="text-lg font-semibold">{c.title}</h3>
                        <p className="text-sm text-muted-foreground">{c.artistName}</p>
                        <p className="text-xs text-muted-foreground">
                          Suggested day {c.date} · not a scheduled post
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[...c.styles, ...c.themes].slice(0, 4).map((t, i) => (
                          <span
                            key={`${t}-${i}`}
                            className="rounded-md border px-2 py-1 text-xs text-muted-foreground"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {c.selectionReason}
                      </p>
                      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            aria-label={`Select ${c.title}`}
                            className="h-4 w-4"
                            type="checkbox"
                            checked={checks.includes(c.candidateSha256)}
                            disabled={status !== 'candidate_review' || busy}
                            onChange={(e) =>
                              setChecks(
                                e.target.checked
                                  ? [...checks, c.candidateSha256]
                                  : checks.filter((h) => h !== c.candidateSha256)
                              )
                            }
                          />
                          Select
                        </label>
                      </div>
                    </article>
                  )
                })}
              </div>
              {!!week.costs?.length && (
                <details className="rounded-xl border bg-card p-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    Production budget & receipts
                  </summary>
                  <div className="mt-3 space-y-2 text-xs leading-relaxed">
                    <p>
                      Reserved $
                      {(week.costs.reduce((n, c) => n + c.max_external_cents, 0) / 100).toFixed(2)}{' '}
                      · known usage-derived tariff $
                      {week.costs.reduce((n, c) => n + (c.receipt?.tariffUsd || 0), 0).toFixed(4)} ·{' '}
                      {week.costs.filter((c) => !c.receipt).length} unknown receipts. These are not
                      provider invoices. Cloud/storage charges are separate; unknown holds are not
                      refunded.
                    </p>
                    {week.costs.map((c, i) => (
                      <p key={i}>
                        {c.stage}: held ${(c.max_external_cents / 100).toFixed(2)} /{' '}
                        {c.receipt
                          ? `$${c.receipt.tariffUsd.toFixed(4)} estimated tariff`
                          : 'billing outcome unknown'}
                      </p>
                    ))}
                  </div>
                </details>
              )}
              {!!week.proposal.blocked?.length && (
                <details className="rounded-xl border bg-card p-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    Excluded artwork ideas ({week.proposal.blocked.length})
                  </summary>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Missing, stale, previously proposed or insufficiently grounded works were
                    excluded—not silently substituted.
                  </p>
                  {week.proposal.blocked.map((b, i) => (
                    <p key={i} className="mt-2 text-xs">
                      {b.handle || b.artworkId || 'Unresolved artwork'} · {b.reason}
                    </p>
                  ))}
                </details>
              )}
            </>
          )}
          <details className="rounded-xl border bg-muted/20 p-4">
            <summary className="cursor-pointer text-sm font-medium">
              Discovery activity & operator tools
            </summary>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                {discovery.data
                  ? discovery.data.enabled
                    ? 'Weekly discovery scheduler enabled. Candidates still require review.'
                    : 'Discovery scheduler paused.'
                  : 'Discovery status unavailable.'}{' '}
                Discovery does not approve generation or publication.
              </p>
              {discovery.data?.runs.map((r) => (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm"
                >
                  <span>
                    Week of {r.week_start.slice(0, 10)} · {r.status.replace(/_/g, ' ')}
                    {r.error && <span className="block text-xs">{r.error}</span>}
                  </span>
                  {r.programme_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelected(r.programme_id!)}
                    >
                      Open ideas
                    </Button>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm">
                  Week start
                  <input
                    aria-label="Discovery week start"
                    className="mt-1 block min-h-11 rounded-lg border bg-background px-3"
                    type="date"
                    value={discoveryWeek}
                    onChange={(e) => setDiscoveryWeek(e.target.value)}
                  />
                </label>
                <Button
                  variant="outline"
                  disabled={busy || !discoveryWeek || !discovery.data?.enabled}
                  onClick={() =>
                    void action(async () => {
                      await apiClient.post(`${base}/discovery/runs`, { weekStart: discoveryWeek })
                      setMessage('Discovery requested. Review its results before creating a world.')
                    })
                  }
                >
                  Discover review batch
                </Button>
              </div>
              <details>
                <summary className="cursor-pointer text-sm">
                  Advanced: import immutable discovery JSON
                </summary>
                <textarea
                  aria-label="Weekly discovery proposal JSON"
                  className="mt-3 h-32 w-full rounded border bg-background p-3 font-mono text-xs"
                  value={proposal}
                  onChange={(e) => setProposal(e.target.value)}
                />
                <Button
                  variant="outline"
                  disabled={!proposal || busy}
                  onClick={() =>
                    void action(async () => {
                      const { data } = await apiClient.post<Programme>(base, JSON.parse(proposal), {
                        headers: { 'Idempotency-Key': crypto.randomUUID() },
                      })
                      setSelected(data.id)
                      setMessage(
                        'Imported artwork ideas; no production approval or dispatch occurred.'
                      )
                    })
                  }
                >
                  Import week
                </Button>
              </details>
            </div>
          </details>
        </>
      )}
      <Dialog
        open={confirmOpen}
        onOpenChange={(value) => {
          if (!busy) setConfirmOpen(value)
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-y-auto font-sans">
          <DialogHeader>
            <DialogTitle>
              Create {picked.length} {picked.length === 1 ? 'world' : 'worlds'}
            </DialogTitle>
            <DialogDescription>
              One approval starts managed creation. The system handles source checks and production.
              Choose films separately after the worlds are built. Nothing is posted.
            </DialogDescription>
          </DialogHeader>
          <>
            <ul className="space-y-2 text-sm">
              {picked.map((c) => (
                <li
                  key={c.candidateSha256}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3"
                >
                  <div>
                    {c.title}
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {c.artistName} · {c.themes.slice(0, 3).join(' / ')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <label className="text-sm font-medium">
              Weekly production budget (USD)
              <input
                aria-label="Weekly generation budget USD"
                className="mt-2 min-h-11 w-full rounded-lg border bg-background px-3"
                type="number"
                min="0"
                max="25"
                step="0.01"
                disabled={!!week?.budget}
                value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
              />
            </label>
            <p className="text-xs leading-relaxed text-muted-foreground">
              One budget shared by the whole week and all retries, not a per-film allowance. Daily
              visits have music and no narration. Provider invoices and cloud charges are separate;
              unknown costs retain their holds.
            </p>
            <p className="text-sm text-muted-foreground">
              {creationEnabled
                ? 'Next: automatic world build → world library & QR preview → optional Create film → your film review & scheduling.'
                : 'Production is currently paused by the administrator. You can save your creation approval now; no generation will start until production is enabled.'}
            </p>
            {message && (
              <p role="status" className="text-sm">
                {message}
              </p>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" disabled={busy} onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={
                  busy ||
                  !week ||
                  !checks.length ||
                  !Number.isFinite(budget) ||
                  budget <= 0 ||
                  budget > 25
                }
                onClick={() =>
                  void action(async () => {
                    const { data: result } = await apiClient.post<{
                      queued: number
                      waiting: number
                    }>(`${base}/${selected}/review`, {
                      proposalSha256: week!.proposal_sha256,
                      candidateHashes: checks,
                      verdict: 'approve',
                      creationMode: 'managed-world-v1',
                      budget: {
                        maxExternalCents: Math.round(budget * 100),
                        maxNarrationCharacters: week?.budget?.maxNarrationCharacters || 0,
                      },
                      publicationApproved: false,
                    })
                    setConfirmOpen(false)
                    setChecks([])
                    setMessage(
                      creationEnabled
                        ? `${result.queued || 0} world builds queued. ${result.waiting || 0} waiting for runner activation. Follow live steps in Worlds. Films and posts are not created automatically.`
                        : 'Creation approved and saved as waiting. Production is paused; no vendor job was dispatched and no post was created.'
                    )
                  })
                }
              >
                {busy
                  ? 'Saving approval…'
                  : creationEnabled
                    ? 'Approve & build worlds'
                    : 'Approve · save for later'}
              </Button>
            </DialogFooter>
            {checks.length > 0 && (
              <Button
                variant="ghost"
                disabled={busy || !week}
                onClick={() =>
                  void action(async () => {
                    await apiClient.post(`${base}/${selected}/review`, {
                      proposalSha256: week!.proposal_sha256,
                      candidateHashes: checks,
                      verdict: 'reject',
                      publicationApproved: false,
                    })
                    setConfirmOpen(false)
                    setChecks([])
                    setMessage('Selected ideas declined. No production started.')
                  })
                }
              >
                Decline selected ideas instead
              </Button>
            )}
          </>
        </DialogContent>
      </Dialog>
    </section>
  )
}
